"use client";

import { usePostHog } from "@posthog/react";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { parseError } from "evlog";
import { useCallback, useRef } from "react";

import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldTitle,
} from "#/components/ui/field";
import { Spinner } from "#/components/ui/spinner";
import { Switch } from "#/components/ui/switch";
import { updateEndTurnConfirmationInputSchema } from "#/lib/playerSettingsSchemas";
import { patchMyPlayerProfileQuery } from "#/lib/queries";
import { updateMyEndTurnConfirmation } from "#/server/updateMyEndTurnConfirmation";

import type { ProfileUserId } from "./-profile.types";

const endTurnConfirmationSwitchId = "profile-end-turn-confirmation";

function useEndTurnConfirmationForm({
  userId,
  initialEnabled,
}: {
  userId: ProfileUserId;
  initialEnabled: boolean;
}) {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const router = useRouter();
  const savedValueRef = useRef(initialEnabled);

  const form = useForm({
    defaultValues: { enabled: initialEnabled },
  });

  const persistIfChanged = useCallback(
    async ({
      value,
      fieldApi,
    }: {
      value: boolean;
      fieldApi: { setValue: (value: boolean) => void };
    }) => {
      if (value === savedValueRef.current) return undefined;

      try {
        await updateMyEndTurnConfirmation({ data: { enabled: value } });
        savedValueRef.current = value;
        patchMyPlayerProfileQuery({
          queryClient,
          userId,
          patch: { endTurnConfirmation: value },
        });
        void router.invalidate();
        return undefined;
      } catch (err) {
        fieldApi.setValue(savedValueRef.current);
        const parsed = parseError(err);
        if (parsed.status >= 500) {
          posthog.captureException(err);
        }
        return parsed.message;
      }
    },
    [posthog, queryClient, router, userId],
  );

  return { form, persistIfChanged };
}

function EndTurnSwitch({
  field,
}: {
  field: {
    state: { value: boolean; meta: { isValidating: boolean; errors: ReadonlyArray<unknown> } };
    handleChange: (value: boolean) => void;
  };
}) {
  const hasError = field.state.meta.errors.length > 0;

  return (
    <span className="flex shrink-0 items-center gap-2 self-center">
      {field.state.meta.isValidating ? <Spinner className="text-muted-foreground" /> : null}
      <Switch
        id={endTurnConfirmationSwitchId}
        checked={field.state.value}
        disabled={field.state.meta.isValidating}
        onCheckedChange={(checked) => field.handleChange(checked)}
        aria-label="End Turn Confirmation"
        aria-invalid={hasError}
      />
    </span>
  );
}

export function EndTurnConfirmationField({
  userId,
  initialEnabled,
}: {
  userId: ProfileUserId;
  initialEnabled: boolean;
}) {
  const { form, persistIfChanged } = useEndTurnConfirmationForm({ userId, initialEnabled });

  return (
    <form.Field
      name="enabled"
      validators={{
        onChange: updateEndTurnConfirmationInputSchema.shape.enabled,
        onChangeAsync: persistIfChanged,
      }}
    >
      {(field) => {
        const hasError = field.state.meta.errors.length > 0;

        return (
          <Field
            orientation="horizontal"
            className="items-center justify-between gap-4"
            data-invalid={hasError}
          >
            <FieldContent className="min-w-0 flex-1">
              <FieldTitle>End Turn Confirmation</FieldTitle>
              <FieldDescription>
                Show a warning if you try to end turn with moves left.
              </FieldDescription>
              {hasError ? (
                <FieldError
                  errors={field.state.meta.errors as Array<{ message?: string } | undefined>}
                />
              ) : null}
            </FieldContent>
            <EndTurnSwitch field={field} />
          </Field>
        );
      }}
    </form.Field>
  );
}
